import React, { useState, useRef, useEffect } from 'react';
import { RotateCw, Crop, Sliders, Palette, Check, X, Sparkles } from 'lucide-react';
import { ImageFilter, ImageAdjustments } from '../../types';

interface ImageEditorProps {
  imageUrl: string;
  onSave: (editedImageUrl: string, adjustments: ImageAdjustments) => void;
  onCancel: () => void;
}

type EditorTab = 'filters' | 'adjust' | 'crop';

const filters: ImageFilter[] = [
  { name: 'Original', filter: 'none', preview: '' },
  { name: 'Vintage', filter: 'sepia(0.6) contrast(1.1) brightness(1.1)', preview: '' },
  { name: 'B&W', filter: 'grayscale(1) contrast(1.2)', preview: '' },
  { name: 'Cool', filter: 'hue-rotate(180deg) saturate(1.3) brightness(1.1)', preview: '' },
  { name: 'Warm', filter: 'hue-rotate(30deg) saturate(1.4) brightness(1.2)', preview: '' },
  { name: 'Bright', filter: 'brightness(1.4) contrast(1.2) saturate(1.1)', preview: '' },
  { name: 'Dramatic', filter: 'contrast(1.6) brightness(0.9) saturate(1.3)', preview: '' },
  { name: 'Soft', filter: 'blur(0.3px) brightness(1.1) contrast(0.9)', preview: '' },
  { name: 'Vibrant', filter: 'saturate(1.8) contrast(1.1) brightness(1.05)', preview: '' },
  { name: 'Fade', filter: 'contrast(0.8) brightness(1.2) saturate(0.8)', preview: '' },
  { name: 'Sunset', filter: 'sepia(0.3) hue-rotate(15deg) saturate(1.5) brightness(1.1)', preview: '' },
  { name: 'Ocean', filter: 'hue-rotate(200deg) saturate(1.4) contrast(1.1)', preview: '' }
];

export const ImageEditor: React.FC<ImageEditorProps> = ({
  imageUrl,
  onSave,
  onCancel
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewRef = useRef<HTMLImageElement>(null);
  const [activeTab, setActiveTab] = useState<EditorTab>('filters');
  const [currentImageUrl, setCurrentImageUrl] = useState(imageUrl);
  const [selectedFilter, setSelectedFilter] = useState(filters[0]);
  const [adjustments, setAdjustments] = useState<ImageAdjustments>({
    brightness: 100,
    contrast: 100,
    saturation: 100,
    rotation: 0
  });

  // Apply filter to preview image
  useEffect(() => {
    if (previewRef.current && activeTab === 'filters') {
      previewRef.current.style.filter = selectedFilter.filter;
    }
  }, [selectedFilter, activeTab]);

  const applyFilterToCanvas = async (filter: ImageFilter): Promise<string> => {
    return new Promise((resolve, reject) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        reject(new Error('Canvas not available'));
        return;
      }

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }

      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      img.onload = () => {
        // Set canvas dimensions to match image
        canvas.width = img.width;
        canvas.height = img.height;

        // Clear canvas and apply filter
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.filter = filter.filter;
        ctx.drawImage(img, 0, 0);

        // Convert to data URL
        try {
          const filteredImageUrl = canvas.toDataURL('image/jpeg', 0.9);
          resolve(filteredImageUrl);
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image'));
      };

      img.src = currentImageUrl;
    });
  };

  const applyAdjustments = () => {
    const { brightness, contrast, saturation, rotation } = adjustments;
    const adjustmentFilter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      
      ctx.save();
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      
      // Combine base filter with adjustments
      const combinedFilter = selectedFilter.filter === 'none' 
        ? adjustmentFilter 
        : `${selectedFilter.filter} ${adjustmentFilter}`;
      
      ctx.filter = combinedFilter;
      ctx.drawImage(img, -img.width / 2, -img.height / 2);
      ctx.restore();
    };
    
    img.src = currentImageUrl;
  };

  useEffect(() => {
    if (activeTab === 'adjust') {
      applyAdjustments();
    }
  }, [adjustments, activeTab, currentImageUrl, selectedFilter]);

  const handleFilterSelect = (filter: ImageFilter) => {
    setSelectedFilter(filter);
  };

  const handleApplyFilter = async () => {
    try {
      let filteredImageUrl: string;
      
      if (selectedFilter.name === 'Original') {
        filteredImageUrl = imageUrl;
      } else {
        filteredImageUrl = await applyFilterToCanvas(selectedFilter);
      }
      
      setCurrentImageUrl(filteredImageUrl);
      setActiveTab('adjust'); // Move to adjustments after applying filter
    } catch (error) {
      console.error('Error applying filter:', error);
      alert('Failed to apply filter. Please try again.');
    }
  };

  const handleSave = () => {
    if (activeTab === 'adjust') {
      const canvas = canvasRef.current;
      if (canvas) {
        const editedImageUrl = canvas.toDataURL('image/jpeg', 0.9);
        onSave(editedImageUrl, { 
          ...adjustments, 
          appliedFilter: selectedFilter.name 
        });
      } else {
        onSave(currentImageUrl, { 
          ...adjustments, 
          appliedFilter: selectedFilter.name 
        });
      }
    } else {
      onSave(currentImageUrl, { 
        ...adjustments, 
        appliedFilter: selectedFilter.name 
      });
    }
  };

  const handleAdjustmentChange = (key: keyof ImageAdjustments, value: number) => {
    setAdjustments(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const resetAdjustments = () => {
    setAdjustments({
      brightness: 100,
      contrast: 100,
      saturation: 100,
      rotation: 0
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black text-white">
        <button onClick={onCancel} className="p-2 hover:bg-gray-800 rounded-lg transition-colors">
          <X className="h-6 w-6" />
        </button>
        <div className="text-center">
          <h2 className="text-lg font-medium">Edit Photo</h2>
          {selectedFilter.name !== 'Original' && (
            <p className="text-sm text-gray-300">Filter: {selectedFilter.name}</p>
          )}
        </div>
        <button 
          onClick={handleSave} 
          className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
        >
          <Check className="h-6 w-6" />
        </button>
      </div>

      {/* Image Preview */}
      <div className="flex-1 flex items-center justify-center p-4">
        {activeTab === 'filters' ? (
          <div className="relative max-w-md w-full">
            <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden mb-4">
              <img
                ref={previewRef}
                src={currentImageUrl}
                alt="Filter preview"
                className="w-full h-full object-cover transition-all duration-300"
                onError={(e) => {
                  console.error('Preview image failed to load:', currentImageUrl);
                  e.currentTarget.style.display = 'none';
                }}
              />
            </div>
          </div>
        ) : (
          <div className="relative max-w-full max-h-full">
            <canvas
              ref={canvasRef}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
            {activeTab !== 'adjust' && (
              <img
                src={currentImageUrl}
                alt="Preview"
                className="max-w-full max-h-full object-contain rounded-lg"
              />
            )}
          </div>
        )}
      </div>

      {/* Tools Tabs */}
      <div className="bg-gray-900 text-white">
        <div className="flex border-b border-gray-700">
          <button
            onClick={() => setActiveTab('filters')}
            className={`flex-1 py-3 px-4 text-center transition-colors ${
              activeTab === 'filters' ? 'bg-purple-600' : 'hover:bg-gray-800'
            }`}
          >
            <Sparkles className="h-5 w-5 mx-auto mb-1" />
            <span className="text-xs">Filters</span>
          </button>
          <button
            onClick={() => setActiveTab('adjust')}
            className={`flex-1 py-3 px-4 text-center transition-colors ${
              activeTab === 'adjust' ? 'bg-purple-600' : 'hover:bg-gray-800'
            }`}
          >
            <Sliders className="h-5 w-5 mx-auto mb-1" />
            <span className="text-xs">Adjust</span>
          </button>
          <button
            onClick={() => setActiveTab('crop')}
            className={`flex-1 py-3 px-4 text-center transition-colors ${
              activeTab === 'crop' ? 'bg-purple-600' : 'hover:bg-gray-800'
            }`}
          >
            <Crop className="h-5 w-5 mx-auto mb-1" />
            <span className="text-xs">Crop</span>
          </button>
        </div>

        {/* Tool Content */}
        <div className="p-4 max-h-96 overflow-y-auto">
          {activeTab === 'filters' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-white text-sm font-medium">Choose a filter</h4>
                <span className="text-xs text-gray-400">{filters.length} filters available</span>
              </div>
              
              {/* Filter Grid - Responsive Layout */}
              <div className="grid grid-cols-4 gap-3">
                {filters.map((filter) => (
                  <button
                    key={filter.name}
                    onClick={() => handleFilterSelect(filter)}
                    className={`relative group rounded-lg overflow-hidden transition-all duration-200 ${
                      selectedFilter.name === filter.name
                        ? 'ring-2 ring-purple-500'
                        : 'hover:ring-2 hover:ring-gray-400'
                    }`}
                  >
                    <div className="aspect-square bg-gray-100 overflow-hidden">
                      <img
                        src={currentImageUrl}
                        alt={filter.name}
                        className="w-full h-full object-cover"
                        style={{ filter: filter.filter }}
                        onError={(e) => {
                          console.error('Filter preview failed to load:', currentImageUrl);
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    </div>
                    
                    {/* Filter Name */}
                    <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-75 text-white p-1">
                      <p className="text-xs font-medium text-center truncate">{filter.name}</p>
                    </div>

                    {/* Selected Indicator */}
                    {selectedFilter.name === filter.name && (
                      <div className="absolute top-1 right-1 bg-purple-600 text-white rounded-full p-1">
                        <Check className="h-2 w-2" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
              
              {/* Apply Filter Button */}
              <button
                onClick={handleApplyFilter}
                className="w-full mt-4 bg-purple-600 hover:bg-purple-700 text-white py-3 px-4 rounded-lg transition-colors font-medium"
              >
                Apply {selectedFilter.name} Filter
              </button>

              {/* Filter Description */}
              <div className="bg-gray-800 rounded-lg p-3 mt-3">
                <h5 className="text-white text-sm font-medium mb-1">{selectedFilter.name}</h5>
                <p className="text-gray-300 text-xs">
                  {selectedFilter.name === 'Original' && 'No filter applied - original image'}
                  {selectedFilter.name === 'Vintage' && 'Warm, nostalgic sepia tone with enhanced contrast'}
                  {selectedFilter.name === 'B&W' && 'Classic black and white with increased contrast'}
                  {selectedFilter.name === 'Cool' && 'Cool blue tones with enhanced saturation'}
                  {selectedFilter.name === 'Warm' && 'Golden warm tones perfect for portraits'}
                  {selectedFilter.name === 'Bright' && 'Enhanced brightness and vibrant colors'}
                  {selectedFilter.name === 'Dramatic' && 'High contrast for bold, striking images'}
                  {selectedFilter.name === 'Soft' && 'Gentle blur for dreamy, romantic feel'}
                  {selectedFilter.name === 'Vibrant' && 'Boosted saturation for vivid colors'}
                  {selectedFilter.name === 'Fade' && 'Faded film look with reduced contrast'}
                  {selectedFilter.name === 'Sunset' && 'Golden hour vibes with warm sepia'}
                  {selectedFilter.name === 'Ocean' && 'Deep blue tones inspired by the sea'}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'adjust' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium">Adjustments</h3>
                <button
                  onClick={resetAdjustments}
                  className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Reset
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Brightness: {adjustments.brightness}%
                </label>
                <input
                  type="range"
                  min="50"
                  max="150"
                  value={adjustments.brightness}
                  onChange={(e) => handleAdjustmentChange('brightness', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Contrast: {adjustments.contrast}%
                </label>
                <input
                  type="range"
                  min="50"
                  max="150"
                  value={adjustments.contrast}
                  onChange={(e) => handleAdjustmentChange('contrast', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Saturation: {adjustments.saturation}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="200"
                  value={adjustments.saturation}
                  onChange={(e) => handleAdjustmentChange('saturation', parseInt(e.target.value))}
                  className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer slider"
                />
              </div>

              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Rotation</span>
                <button
                  onClick={() => handleAdjustmentChange('rotation', (adjustments.rotation + 90) % 360)}
                  className="p-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  <RotateCw className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {activeTab === 'crop' && (
            <div className="text-center text-gray-400">
              <Crop className="h-8 w-8 mx-auto mb-2" />
              <p>Crop functionality coming soon</p>
              <p className="text-xs mt-1">Use the adjustments tab to fine-tune your image</p>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #9C6ADE;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .slider::-moz-range-thumb {
          height: 20px;
          width: 20px;
          border-radius: 50%;
          background: #9C6ADE;
          cursor: pointer;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
      `}</style>
    </div>
  );
};